import {
  getPasswordResetPageUrl,
  getUserLogInUrl,
  getUserLogOutUrl,
  getUserPageUrl,
  getUserSignUpUrl,
} from '@/utils/getUserAuthUrl';
import { mergeStable } from '@/utils/merge';
import useCookies from 'lib/hooks/useCookies';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useReducer, useState } from 'react';

type Urls = {
  logInUrl: string;
  logOutUrl: string;
  signUpUrl: string;
  myPageUrl: string;
};

const getUrls = (state: Urls, returnTo: string) => {
  const params = returnTo ? { return_to: returnTo } : undefined;

  const urls = {
    logInUrl: getUserLogInUrl(params),
    logOutUrl: getUserLogOutUrl(params),
    signUpUrl: getUserSignUpUrl(params),
    myPageUrl: getUserPageUrl(),
    passwordResetUrl: getPasswordResetPageUrl(),
  };

  return mergeStable(state, urls);
};

const isValidCookie = (cookie: string | undefined) => Boolean(cookie && cookie.length > 0);

export type UserAuthProps = ReturnType<typeof useUserAuth>;

type UseUserAuthOptions = {
  customReturnTo?: string;
};

const useUserAuth = (options?: UseUserAuthOptions) => {
  const pathname = usePathname();
  const [{ has_session }] = useCookies('has_session');
  const [isUserLoggedIn, setUserLoggedIn] = useState(false);
  const [urls, setReturnTo] = useReducer(getUrls, getUrls({} as Urls, ''));

  useEffect(() => {
    const { customReturnTo } = options || {};
    const siteUrl = window.location.origin;
    const isStartPage = pathname === '/';
    const defaultReturnTo = pathname.startsWith('/mina-sidor')
      ? siteUrl
      : isStartPage
        ? `${siteUrl}/mina-sidor`
        : `${siteUrl}${pathname}`;

    setReturnTo(customReturnTo || defaultReturnTo);
  }, [pathname, options?.customReturnTo]);

  useEffect(() => {
    setUserLoggedIn(isValidCookie(has_session));
  }, [has_session]);

  return useMemo(
    () => ({
      isUserLoggedIn,
      ...urls,
    }),
    [isUserLoggedIn, urls],
  );
};

export default useUserAuth;
